import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { bookingApi } from '../api/bookingApi';
import { CancelBookingDto, CreateBookingDto, RescheduleBookingDto, UpdateBookingDto } from '../types/booking';

const QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
  staleTime: 60 * 1000,
} as const;

const QUERY_KEYS = {
  bookings: ['bookings'],
  upcoming: ['bookings-upcoming'],
  booking: (id: string) => ['booking', id],
};

export const useBookings = (
  enabled = true
) =>
  useQuery({
    queryKey: QUERY_KEYS.bookings,
    queryFn: () =>
      bookingApi.getBookings().then((res) => res.data),
    ...QUERY_OPTIONS,
    enabled,
  });

export const useUpcomingBookings = () =>
  useQuery({
    queryKey: QUERY_KEYS.upcoming,
    queryFn: () => bookingApi.getUpcomingBookings().then((res) => res.data),
    ...QUERY_OPTIONS,
  });

export const useBooking = (bookingId: string) =>
  useQuery({
    queryKey: QUERY_KEYS.booking(bookingId),
    queryFn: () => bookingApi.getBooking(bookingId).then((res) => res.data),
    enabled: Boolean(bookingId),
    ...QUERY_OPTIONS,
  });

const invalidateAll = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.bookings });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.upcoming });
};

export const useCreateBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBookingDto) => bookingApi.createBooking(data).then((res) => res.data),
    onSuccess: () => {
      invalidateAll(queryClient);
      toast.success('Booking created successfully');
    },
    onError: () => toast.error('Failed to create booking'),
  });
};

export const useUpdateBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBookingDto }) => bookingApi.updateBooking(id, data).then((res) => res.data),
    onSuccess: (_data, variables) => {
      invalidateAll(queryClient);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.booking(variables.id) });
      toast.success('Booking updated successfully');
    },
    onError: () => toast.error('Failed to update booking'),
  });
};

export const useCancelBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CancelBookingDto }) => bookingApi.cancelBooking(id, data).then((res) => res.data),
    onSuccess: (_data, variables) => {
      invalidateAll(queryClient);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.booking(variables.id) });
      toast.success('Booking cancelled');
    },
    onError: () => toast.error('Failed to cancel booking'),
  });
};

export const useRescheduleBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RescheduleBookingDto }) => bookingApi.rescheduleBooking(id, data).then((res) => res.data),
    onSuccess: (_data, variables) => {
      invalidateAll(queryClient);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.booking(variables.id) });
      toast.success('Booking rescheduled');
    },
    onError: () => toast.error('Failed to reschedule booking'),
  });
};

export const useCompleteBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => bookingApi.completeBooking(id).then((res) => res.data),
    onSuccess: (_data, id) => {
      invalidateAll(queryClient);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.booking(id) });
      toast.success('Booking marked as complete');
    },
    onError: () => toast.error('Failed to complete booking'),
  });
};

export const useDeleteBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => bookingApi.deleteBooking(id),
    onSuccess: () => {
      invalidateAll(queryClient);
      toast.success('Booking deleted successfully');
    },
    onError: () => toast.error('Failed to delete booking'),
  });
};

